import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App.jsx';
import Login from './pages/Login.jsx';
import DisplayKiosk from './pages/DisplayKiosk.jsx';
import supabase from './supabase/supabase.js';
import './index.css';

/**
 * AUTH CALLBACK LOADER
 * 
 * Intercepts Supabase's implicit auth tokens BEFORE HashRouter boots.
 * HashRouter will aggressively overwrite `#access_token=...` because it 
 * doesn't match any route. This component pauses rendering, lets Supabase 
 * absorb the token securely, sets a session flag, and then safely formats 
 * the URL back to a valid router path.
 */
function AuthCallbackLoader({ children }) {
  const [isProcessing, setIsProcessing] = useState(() => {
    return window.location.hash.includes('access_token=');
  });

  useEffect(() => {
    if (!isProcessing) return;

    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      sessionStorage.setItem('st_recovery', '1');
    } else if (hash.includes('type=signup') || hash.includes('type=email')) {
      sessionStorage.setItem('st_email_verified', '1');
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        // Token absorbed successfully. Reset hash to a valid route and unlock app.
        setTimeout(() => {
          window.location.hash = '#/login';
          setIsProcessing(false);
        }, 100);
      }
    });

    // Failsafe timeout: If token is expired/invalid, unlock the app anyway after 3s
    const t = setTimeout(() => {
      window.location.hash = '#/login';
      setIsProcessing(false);
    }, 3000);

    return () => {
      authListener.subscription?.unsubscribe();
      clearTimeout(t);
    };
  }, [isProcessing]);

  if (isProcessing) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#000000] text-white">
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#666666]">
          Authenticating...
        </div>
      </div>
    );
  }

  return children;
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AuthCallbackLoader>
      <HashRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/admin-dash" element={<App />} />
          <Route path="/display/:roomId" element={<DisplayKiosk />} />
        </Routes>
      </HashRouter>
    </AuthCallbackLoader>
  </React.StrictMode>
);